import { type ReactElement } from 'react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { faChevronLeft, faFileLines, faPen, faPlus, faTrashCan } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { AIAssistantClientSkill, AIAssistantClientSkillInput } from 'balus-common';
import YAMLDefault from 'yaml';
import type * as YAMLModule from 'yaml';
import { buildSkillDefinitionText } from './AIAssistantSkillDefinitionText';

const MAX_CLIENT_SKILLS = 20;
const FRONTMATTER_REGEX = /^---\s*\n([\s\S]*?)---\s*\n?/;
const YAML = YAMLDefault as typeof YAMLModule;
const { parse: parseYaml } = YAML;

export type SkillDraft = {
  definitionText: string;
  originalSkill: AIAssistantClientSkill | null;
};

export type SkillMutationRequest =
  | { type: 'create'; input: AIAssistantClientSkillInput }
  | { type: 'update'; expectedSkill: AIAssistantClientSkill; input: AIAssistantClientSkillInput }
  | { type: 'setEnabled'; expectedSkill: AIAssistantClientSkill; enabled: boolean }
  | { type: 'delete'; expectedSkill: AIAssistantClientSkill };

function buildEmptyDraft(): SkillDraft {
  return {
    definitionText: '',
    originalSkill: null,
  };
}

function buildDraft(skill: AIAssistantClientSkill): SkillDraft {
  return {
    definitionText: buildSkillDefinitionText(skill),
    originalSkill: skill,
  };
}

function parseSkillDefinitionText(text: string): AIAssistantClientSkillInput {
  const match = text.match(FRONTMATTER_REGEX);
  if (!match) {
    throw new Error('Skill definition must start with YAML frontmatter');
  }

  const parsedYaml = parseYaml(match[1] ?? '');
  if (parsedYaml !== null && parsedYaml !== undefined && !isRecord(parsedYaml)) {
    throw new Error('Skill frontmatter must be an object');
  }

  const frontmatter: Record<string, unknown> = parsedYaml ?? {};
  const name = readOptionalString(frontmatter.name).trim();

  return {
    enabled: typeof frontmatter.enabled === 'boolean' ? frontmatter.enabled : true,
    name,
    description: readOptionalString(frontmatter.description).trim(),
    triggers: readTriggers(frontmatter.triggers, name),
    whenToUse: readOptionalString(frontmatter.whenToUse).trim(),
    argumentHint: readOptionalString(frontmatter.argumentHint).trim(),
    prompt: text.slice(match[0].length).trim(),
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function readOptionalString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function readTriggers(value: unknown, name: string): string[] {
  const triggers =
    typeof value === 'string'
      ? value.split(/[\s,]+/)
      : Array.isArray(value)
        ? value.filter((item): item is string => typeof item === 'string')
        : [];
  const normalizedTriggers = triggers.map((trigger) => trigger.trim()).filter(Boolean);
  if (normalizedTriggers.length > 0) return normalizedTriggers;
  return name.length > 0 ? [`/${name}`] : [];
}

function AIAssistantSkillDraftForm({
  draft,
  isSaving,
  onDraftChange,
  onCancel,
  onApply,
}: {
  draft: SkillDraft;
  isSaving: boolean;
  onDraftChange: (draft: SkillDraft) => void;
  onCancel: () => void;
  onApply: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const isEditing = draft.originalSkill !== null;

  return (
    <div className="flex min-h-0 grow flex-col">
      <div className="flex items-center gap-2 border-b border-gray-200 py-3">
        <button
          className="grid size-7 shrink-0 place-items-center rounded text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 disabled:pointer-events-none disabled:opacity-50"
          title={t('viewModel.aiChat.backLabel')}
          aria-label={t('viewModel.aiChat.backLabel')}
          onClick={onCancel}
          disabled={isSaving}
          type="button"
        >
          <FontAwesomeIcon icon={faChevronLeft} />
        </button>
        <h2 className="text-base font-bold text-gray-900">
          {isEditing ? t('viewModel.aiSettings.skillEditTitle') : t('viewModel.aiSettings.skillAddTitle')}
        </h2>
      </div>

      <div className="flex min-h-0 grow flex-col overflow-y-auto py-4">
        <label className="flex min-h-[320px] grow flex-col gap-1.5">
          <span className="text-sm font-semibold text-gray-800">{t('viewModel.aiSettings.skillDefinitionLabel')}</span>
          <textarea
            className="min-h-[320px] grow resize-none rounded border border-gray-300 bg-gray-50 p-3 font-mono text-sm leading-6 text-gray-900 outline-none transition focus:border-teal-500 focus:bg-white focus:ring-1 focus:ring-teal-500 disabled:opacity-50"
            aria-label={t('viewModel.aiSettings.skillDefinitionLabel')}
            value={draft.definitionText}
            disabled={isSaving}
            onChange={(e) => {
              onDraftChange({ ...draft, definitionText: e.target.value });
            }}
          />
        </label>
      </div>

      <div className="flex shrink-0 items-center justify-end gap-2 border-t border-gray-200 py-3">
        <button
          className="rounded border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:pointer-events-none disabled:opacity-50"
          onClick={onCancel}
          disabled={isSaving}
          type="button"
        >
          {t('common.action.cancel')}
        </button>
        <button
          className="rounded border border-teal-600 bg-teal-600 px-3 py-1.5 text-sm font-medium text-white hover:border-teal-700 hover:bg-teal-700 disabled:pointer-events-none disabled:opacity-50"
          onClick={onApply}
          disabled={isSaving}
          type="button"
        >
          {isEditing ? t('viewModel.aiSettings.skillUpdate') : t('viewModel.aiSettings.skillCreate')}
        </button>
      </div>
    </div>
  );
}

export function AIAssistantSkillSettings({
  skills,
  draft,
  isSaving,
  onSkillMutation,
  onDraftChange,
}: {
  skills: AIAssistantClientSkill[];
  draft: SkillDraft | null;
  isSaving: boolean;
  onSkillMutation: (mutation: SkillMutationRequest) => Promise<boolean>;
  onDraftChange: (draft: SkillDraft | null) => void;
}): ReactElement {
  const { t } = useTranslation();

  const handleAddDraft = () => {
    if (skills.length >= MAX_CLIENT_SKILLS) {
      toast.error(t('viewModel.aiSettings.skillLimitError'));
      return;
    }
    onDraftChange(buildEmptyDraft());
  };

  const handleApplyDraft = async () => {
    if (!draft) return;

    let input: AIAssistantClientSkillInput;
    try {
      input = parseSkillDefinitionText(draft.definitionText);
    } catch {
      toast.error(t('viewModel.aiSettings.skillDefinitionParseError'));
      return;
    }

    const saved = await onSkillMutation(
      draft.originalSkill === null
        ? { type: 'create', input }
        : { type: 'update', expectedSkill: draft.originalSkill, input }
    );
    if (saved) {
      onDraftChange(null);
    }
  };

  if (draft !== null) {
    return (
      <AIAssistantSkillDraftForm
        draft={draft}
        isSaving={isSaving}
        onDraftChange={onDraftChange}
        onCancel={() => {
          onDraftChange(null);
        }}
        onApply={() => {
          void handleApplyDraft();
        }}
      />
    );
  }

  return (
    <div className="flex min-h-0 grow flex-col">
      <div className="flex shrink-0 items-center border-b border-gray-200 py-3">
        <h2 className="text-base font-bold text-gray-900">{t('viewModel.aiSettings.skillLabel')}</h2>
        <div className="grow" />
        <button
          className="grid size-7 place-items-center rounded text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 disabled:pointer-events-none disabled:opacity-50"
          onClick={handleAddDraft}
          disabled={isSaving}
          title={t('viewModel.aiSettings.skillAdd')}
          aria-label={t('viewModel.aiSettings.skillAdd')}
          type="button"
        >
          <FontAwesomeIcon icon={faPlus} />
        </button>
      </div>

      {skills.length === 0 ? (
        <div className="flex grow flex-col items-center justify-center px-6 pb-20 text-center">
          <div className="text-base font-bold text-gray-900">{t('viewModel.aiSettings.skillEmpty')}</div>
          <p className="mt-3 max-w-[280px] text-sm leading-6 text-gray-600">
            {t('viewModel.aiSettings.skillEmptyDescription')}
          </p>
          <button
            className="mt-5 rounded border border-teal-600 bg-teal-600 px-3 py-1.5 text-sm font-medium text-white hover:border-teal-700 hover:bg-teal-700 disabled:pointer-events-none disabled:opacity-50"
            onClick={handleAddDraft}
            disabled={isSaving}
            type="button"
          >
            {t('viewModel.aiSettings.skillAdd')}
          </button>
        </div>
      ) : (
        <div className="min-h-0 grow overflow-y-auto py-2">
          <div className="divide-y divide-gray-100">
            {skills.map((skill) => (
              <div key={skill.id} className="group flex min-w-0 items-center gap-2 rounded p-2 hover:bg-gray-50">
                <label className="flex min-w-0 flex-1 items-center gap-2">
                  <input
                    type="checkbox"
                    className="form-checkbox shrink-0 disabled:opacity-50"
                    checked={skill.enabled}
                    disabled={isSaving}
                    onChange={(e) => {
                      void onSkillMutation({
                        type: 'setEnabled',
                        expectedSkill: skill,
                        enabled: e.target.checked,
                      });
                    }}
                  />
                  <FontAwesomeIcon icon={faFileLines} className="shrink-0 text-gray-500" />
                  <span className="truncate text-sm font-semibold text-gray-800">{skill.name}</span>
                </label>
                <button
                  className="grid size-7 shrink-0 place-items-center rounded text-xs text-gray-600 hover:bg-white hover:text-gray-900 disabled:pointer-events-none disabled:opacity-50"
                  onClick={() => {
                    onDraftChange(buildDraft(skill));
                  }}
                  disabled={isSaving}
                  title={t('common.action.edit')}
                  aria-label={t('common.action.edit')}
                  type="button"
                >
                  <FontAwesomeIcon icon={faPen} />
                </button>
                <button
                  className="grid size-7 shrink-0 place-items-center rounded text-xs text-red-600 hover:bg-red-50 hover:text-red-800 disabled:pointer-events-none disabled:opacity-50"
                  onClick={() => {
                    void onSkillMutation({ type: 'delete', expectedSkill: skill });
                  }}
                  disabled={isSaving}
                  title={t('common.action.delete')}
                  aria-label={t('common.action.delete')}
                  type="button"
                >
                  <FontAwesomeIcon icon={faTrashCan} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
